"use client";

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SocialAuthButtons } from "../social-buttons";
import { AuthPageHeader } from "../page-header";
import { SubmitButton } from "@/components/ui/button";
import { Input, PasswordInput } from "@/components/ui/input";
import { useLogin } from "../../hooks/use-login";
import { Icon } from "@/components/ui/icon";
import { LinkButton } from "@/components/ui/link-button";
import { showToast } from "@/components/ui/sonner";
import { Alert } from "@/components/ui/alert";
import {
  clearLoginErrorParamFromCurrentUrl,
  clearSignedOutParamFromCurrentUrl,
  getForcedSignOutNotice,
  getLoginErrorNotice,
} from "@/lib/session/session-logout";
import { appendInvitationToken } from "@/lib/invitation-links";

const loginSchema = z.object({
  email: z.email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginValues = z.infer<typeof loginSchema>;

interface SignInProps {
  forcedSignOutReason?: string | null;
  invitationToken?: string | null;
  loginError?: string | null;
}

export function SignIn({
  forcedSignOutReason,
  invitationToken = null,
  loginError,
}: SignInProps) {
  const login = useLogin();
  const loginErrorNotice = getLoginErrorNotice(loginError);
  const registerHref = appendInvitationToken(
    "/register",
    invitationToken || null,
  );

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    if (!forcedSignOutReason) return;

    const forcedSignOutNotice = getForcedSignOutNotice(forcedSignOutReason);

    if (forcedSignOutNotice) {
      showToast.warning(forcedSignOutNotice.title, {
        description: forcedSignOutNotice.description,
      });
    }

    clearSignedOutParamFromCurrentUrl();
  }, [forcedSignOutReason]);

  useEffect(() => {
    if (!loginError) return;

    clearLoginErrorParamFromCurrentUrl();
  }, [loginError]);

  function onSubmit(values: LoginValues) {
    login.mutate({ email: values.email, password: values.password });
  }

  return (
    <>
      <AuthPageHeader
        title="Welcome to Arbi"
        subtitle="Please sign-in to your account to access Arbi."
      />

      {loginErrorNotice ? (
        <Alert.Container role="alert" variant="warning" dir="vertical">
          <Alert.Icon>
            <Icon name="IconExclamationCircle" size={16} />
          </Alert.Icon>
          <Alert.Title>{loginErrorNotice.title}</Alert.Title>
          <Alert.Content>{loginErrorNotice.description}</Alert.Content>
        </Alert.Container>
      ) : null}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-wrap gap-x-3 gap-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel required>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      variant="auth"
                      size="default"
                      placeholder="mail@neuroscale.ai"
                      autoComplete="email"
                      autoFocus
                      className="rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex items-center justify-between">
                    <FormLabel required>Password</FormLabel>
                    <LinkButton
                      href="/forgot-password"
                      variant="muted-ghost"
                      size="text-xs"
                      tabIndex={-1}
                    >
                      <span className="flex items-center gap-1">
                        Forgot Password
                        <Icon
                          name="IconQuestionmarkCircle2"
                          className="size-3"
                        />
                      </span>
                    </LinkButton>
                  </div>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter password"
                      autoComplete="current-password"
                      className="rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-3 items-center">
            <SubmitButton
              type="submit"
              size="auth"
              variant="linear-ai"
              loading={login.isPending}
              loadingLabel="Signing In"
              className="rounded-sm"
              icon={<Icon name="IconArrowRight" size={16} />}
            >
              Sign In
            </SubmitButton>

            <div className="w-full h-px bg-contrast/10" />
            <SocialAuthButtons variant="signin" />
            <div className="w-full h-px bg-contrast/10" />

            <p className="text-sm text-contrast/70 leading-5">
              Don&apos;t have an account?{" "}
              <Link
                href={registerHref}
                className="font-medium text-heading hover:underline"
              >
                Register
              </Link>
            </p>
          </div>
        </form>
      </Form>

      <p className="text-xs text-contrast/40 leading-normal">
        By proceeding you acknowledge that you have read, understood and agree
        to our{" "}
        <span className="underline decoration-solid">Terms and Conditions</span>
        .
      </p>
    </>
  );
}
